import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import crm, { STAGES } from '../crm/api';
import LeadDetail from '../crm/LeadDetail';
import LeadCaptureModal from '../crm/LeadCaptureModal';
import LeadGrid from '../crm/LeadGrid';
import LeadLedger from '../crm/LeadLedger';
import ChatPanel from '../crm/ChatPanel';

export default function MyLeads() {
  const nav = useNavigate();
  const [name, setName] = useState('Employee');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [captureLead, setCaptureLead] = useState(null); // lead being enriched via the quick popup
  const [view, setView] = useState(
    ['grid', 'list'].includes(new URLSearchParams(window.location.search).get('view'))
      ? new URLSearchParams(window.location.search).get('view')
      : 'list'
  );

  useEffect(() => {
    if (!localStorage.getItem('employee_id')) { nav('/employee/login'); return; }
    setName(localStorage.getItem('employee_name') || 'Employee');
    load();
  }, [nav]);

  const load = () => {
    setLoading(true);
    return crm.get('/leads', { params: { limit: 100 } })
      .then(({ data }) => setLeads(data.leads || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  };

  const logout = () => {
    ['employee_token', 'employee_id', 'employee_name', 'employee_slug', 'crm_token', 'crm_user'].forEach((k) => localStorage.removeItem(k));
    nav('/employee/login');
  };

  const count = (s) => leads.filter((l) => l.status === s).length;
  const kpis = [
    { label: 'Total', value: leads.length, color: '#0b6e3e' },
    { label: 'New', value: count('new'), color: '#0e7490' },
    { label: 'Contacted', value: count('contacted'), color: '#7c3aed' },
    { label: 'Won', value: count('won'), color: '#1a8046' },
  ];

  const filtered = leads.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [l.name, l.company, l.email, l.phone, l.interest].some((v) => (v || '').toLowerCase().includes(s));
  });

  const wa = (phone) => { const p = (phone || '').replace(/[\s\-+]/g, ''); window.open(`https://wa.me/${p.startsWith('91') ? p : '91' + p}`, '_blank'); };

  return (
    <div className="pt-1">
      <div className="max-w-[1180px] space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-4 shadow-sm">
              <div className="text-[13px] text-[var(--muted)]">{k.label}</div>
              <div className="text-2xl font-extrabold mt-1" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* view toggle */}
        <div className="inline-flex rounded-xl p-1" style={{ background: 'var(--soft)' }}>
          {['list', 'grid'].map((v) => (
            <button key={v} onClick={() => setView(v)} className="px-5 py-1.5 rounded-lg text-[13px] font-bold capitalize transition-colors"
              style={view === v ? { background: 'var(--card)', color: 'var(--acc2)', boxShadow: '0 1px 3px rgba(0,0,0,.12)' } : { color: 'var(--muted)' }}>{v}</button>
          ))}
        </div>

        <>
        {/* search + filter chips */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" size={18} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--card)] border border-[var(--border)] rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{ key: 'all', label: 'All' }, ...STAGES].map((s) => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                className={`px-3 py-2 rounded-lg text-[13px] font-semibold border transition-colors ${statusFilter === s.key ? 'text-white border-transparent' : 'bg-[var(--card)] text-[var(--muted)] border-[var(--border)] hover:bg-[var(--soft)]'}`}
                style={statusFilter === s.key ? { background: s.key === 'all' ? '#0b6e3e' : s.color } : {}}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* leads */}
        {loading ? (
          <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] p-12 text-center text-[var(--muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] p-12 text-center">
            <div className="text-4xl mb-2">🗂️</div>
            <div className="font-bold text-[var(--text)]">No leads yet</div>
            <div className="text-sm text-[var(--muted)] mt-1">Leads captured from your card &amp; microsite appear here.</div>
          </div>
        ) : view === 'grid' ? (
          <LeadGrid leads={filtered} onOpen={(id) => setCaptureLead(leads.find((l) => l.id === id) || null)} onWhatsApp={wa} />
        ) : (
          <LeadLedger leads={filtered} onOpen={(id) => setCaptureLead(leads.find((l) => l.id === id) || null)} onWhatsApp={wa} />
        )}
        </>
      </div>

      {/* Quick-enrich popup: record what the customer discussed. "View full details" opens the 360° view. */}
      {captureLead && (
        <LeadCaptureModal
          lead={captureLead}
          onClose={() => setCaptureLead(null)}
          onSaved={load}
          onView={(id) => { setCaptureLead(null); setOpenId(id); }}
        />
      )}
      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      <ChatPanel />
    </div>
  );
}
